import { useEffect } from "react";
import { Button } from "./Button";

interface Props {
  open: boolean;
  secondsLeft: number;
  onStay: () => void;
}

// Non-blocking warning that appears in the last ~30s before idle sign-out.
// Any click anywhere on the dialog (or any activity outside it) counts as
// "stay active" via the underlying useIdleTimeout activity listeners; the
// explicit button just makes the affordance obvious.
export function IdleWarningDialog({ open, secondsLeft, onStay }: Props) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" || e.key === "Enter") onStay();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onStay]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="idle-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4"
      onClick={onStay}
    >
      <div
        className="w-full max-w-sm bg-white rounded-card border border-fh-border shadow-card p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="idle-title" className="font-serif text-[20px] text-fh-ink mb-1.5">
          Still there?
        </h2>
        <p className="text-[14px] text-fh-gray leading-relaxed mb-4">
          You'll be signed out in <strong className="text-fh-ink">{secondsLeft}s</strong>{" "}
          for security. Click below or press a key to stay signed in.
        </p>
        <Button onClick={onStay} fullWidth>
          Stay signed in
        </Button>
      </div>
    </div>
  );
}
